import React from 'react';
import { Container, Box, Typography, useTheme } from '@mui/material';

/**
 * PageContainer Component
 * A consistent container for page content with optional title and actions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title
 * @param {React.ReactNode} props.titleIcon - Icon to display before the title
 * @param {React.ReactNode} props.actions - Actions to display in the header
 * @param {React.ReactNode} props.breadcrumbs - Breadcrumbs to display above the title
 * @param {Object} props.sx - Additional styles
 * @param {boolean} props.fullWidth - Whether to use full width container
 * @returns {React.ReactElement} Page container component
 */
const PageContainer = ({
  children,
  title,
  titleIcon,
  actions,
  breadcrumbs,
  sx = {},
  fullWidth = false,
  maxWidth = 'lg',
  ...rest
}) => {
  const theme = useTheme();
  
  return (
    <Container 
      maxWidth={fullWidth ? false : maxWidth} 
      sx={{ 
        py: 3, 
        ...sx 
      }}
      {...rest}
    >
      {/* Breadcrumbs */}
      {breadcrumbs && (
        <Box mb={2}>
          {breadcrumbs}
        </Box>
      )}
      
      {/* Page Header */}
      {(title || actions) && (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between"
          mb={3}
          pb={1.5}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          {/* Title with optional icon */}
          {title && (
            <Box display="flex" alignItems="center" gap={1}>
              {titleIcon && (
                <Box display="flex" alignItems="center" color="primary.main">
                  {titleIcon}
                </Box>
              )}
              <Typography variant="h4" component="h1" fontWeight="medium">
                {title}
              </Typography>
            </Box>
          )}
          
          {/* Actions */}
          {actions && (
            <Box display="flex" alignItems="center" gap={1}>
              {actions}
            </Box>
          )}
        </Box>
      )}
      
      {/* Page Content */}
      <Box>
        {children}
      </Box>
    </Container>
  );
};

export default PageContainer;